import React, { useContext } from "react";
import { Link as MuiLink, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import {
  KivuI18nContext,
  localizedPath,
  PAGE_ROUTE_ABOUT,
  PAGE_ROUTE_CONTACT,
  PAGE_ROUTE_DOWNLOAD_APPS,
  PAGE_ROUTE_FORGOT_PASSWORD,
  PAGE_ROUTE_MEMBERSHIP_SUBSCRIPTIONS,
  PAGE_ROUTE_REGISTRATION,
  PAGE_ROUTE_ROLES_AND_ACCESS,
  PAGE_ROUTE_USER_GUIDE,
  useKivunovaTranslation,
  PAGE_ROUTE_ABOUT_PRODUCT,
  PAGE_ROUTE_SOPS
} from "@kivunova/kivufrontendcommon";
import { CONTACT_EMAIL } from "../../constants/contactInfo";
import type { FaqItem } from "./types";
import {
  publicInfoBodySx,
  publicInfoLinkSx
} from "./constants/publicInfoPageStyles";

type FaqAnswerProps = {
  item: FaqItem;
};

const FaqAnswer: React.FC<FaqAnswerProps> = ({ item }) => {
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};

  const pathFor = (route: string) =>
    language != null && defaultLang != null
      ? localizedPath(route, language, defaultLang)
      : `/${route}`;

  const registrationPath = pathFor(PAGE_ROUTE_REGISTRATION);
  const forgotPasswordPath = pathFor(PAGE_ROUTE_FORGOT_PASSWORD);
  const downloadAppsPath = pathFor(PAGE_ROUTE_DOWNLOAD_APPS);
  const membershipSubscriptionsPath = pathFor(
    PAGE_ROUTE_MEMBERSHIP_SUBSCRIPTIONS
  );
  const contactPath = pathFor(PAGE_ROUTE_CONTACT);
  const userGuidePath = pathFor(PAGE_ROUTE_USER_GUIDE);
  const aboutPath = pathFor(PAGE_ROUTE_ABOUT);
  const rolesAndAccessPath = pathFor(PAGE_ROUTE_ROLES_AND_ACCESS);
  const aboutProductPath = pathFor(PAGE_ROUTE_ABOUT_PRODUCT);
  const runBooksPath = pathFor(PAGE_ROUTE_SOPS);

  if (item.id === "what-is-fms") {
    return (
      <Typography variant="body1" component="p" sx={publicInfoBodySx}>
        {t(item.answerKey, item.answerFallback)}{" "}
        <MuiLink
          component={Link}
          to={aboutPath}
          underline="hover"
          sx={publicInfoLinkSx}
          data-testid="faq-ingress-about"
        >
          {t("faq-what-is-fms-a-about-link", "Learn more about Kivunova FMS.")}
        </MuiLink>
      </Typography>
    );
  }

  if (item.id === "signup") {
    return (
      <Typography variant="body1" component="p" sx={publicInfoBodySx}>
        {t(
          "faq-signup-a-prefix",
          "From the welcome screen, choose Get started or"
        )}{" "}
        <MuiLink
          component={Link}
          to={registrationPath}
          underline="hover"
          sx={publicInfoLinkSx}
        >
          {t("faq-signup-a-register-link", "Register")}
        </MuiLink>
        {t(
          "faq-signup-a-suffix",
          ", complete company and user details, and verify your email when prompted."
        )}
      </Typography>
    );
  }

  if (item.id === "login-issues") {
    return (
      <Typography variant="body1" component="p" sx={publicInfoBodySx}>
        {t("faq-login-issues-a-prefix", "Use")}{" "}
        <MuiLink
          component={Link}
          to={forgotPasswordPath}
          underline="hover"
          sx={publicInfoLinkSx}
        >
          {t("faq-login-issues-a-forgot-link", "Forgot password")}
        </MuiLink>{" "}
        {t(
          "faq-login-issues-a-suffix",
          "to reset your password. Make sure your account is active and you are using the correct company email. Contact us if the problem continues."
        )}
      </Typography>
    );
  }

  if (item.id === "desktop-mobile") {
    return (
      <Typography variant="body1" component="p" sx={publicInfoBodySx}>
        {t("faq-desktop-mobile-a-prefix", "Yes. Visit the")}{" "}
        <MuiLink
          component={Link}
          to={downloadAppsPath}
          underline="hover"
          sx={publicInfoLinkSx}
        >
          {t("faq-desktop-mobile-a-download-link", "Download apps")}
        </MuiLink>{" "}
        {t(
          "faq-desktop-mobile-a-suffix",
          "for computer apps (Windows and Mac) and phone apps when available. The website version uses the same account."
        )}
      </Typography>
    );
  }

  if (item.id === "billing") {
    return (
      <Typography variant="body1" component="p" sx={publicInfoBodySx}>
        {t("faq-billing-a-prefix", "Plans and subscriptions are available in ")}
        <MuiLink
          component={Link}
          to={membershipSubscriptionsPath}
          underline="hover"
          sx={publicInfoLinkSx}
        >
          {t("faq-billing-a-subscriptions-link", "Subscriptions")}
        </MuiLink>
        {t(
          "faq-billing-a-suffix",
          " after you sign in. Each plan includes different features and limits for your business."
        )}
      </Typography>
    );
  }

  if (item.id === "training") {
    return (
      <Typography variant="body1" component="p" sx={publicInfoBodySx}>
        {t("faq-training-a-prefix", "Start with the")}{" "}
        <MuiLink
          component={Link}
          to={userGuidePath}
          underline="hover"
          sx={publicInfoLinkSx}
        >
          {t("faq-training-a-user-guide-link", "User guide")}
        </MuiLink>
        {t("faq-training-a-middle-prefix", " and ")}{" "}
        <MuiLink
          component={Link}
          to={runBooksPath}
          underline="hover"
          sx={publicInfoLinkSx}
        >
          {t("faq-training-a-run-books-link", "Run books")}
        </MuiLink>
        {t(
          "faq-training-a-middle",
          " for everyday tasks. Larger businesses can request dedicated training."
        )}
      </Typography>
    );
  }

  if (item.id === "roles") {
    return (
      <Typography variant="body1" component="p" sx={publicInfoBodySx}>
        {t(
          "faq-roles-a-prefix",
          "Kivunova FMS uses company-level and store-level roles such as company admin, store admin, warehouse manager, and analyst."
        )}{" "}
        <MuiLink
          component={Link}
          to={rolesAndAccessPath}
          underline="hover"
          sx={publicInfoLinkSx}
          data-testid="faq-ingress-roles-and-access"
        >
          {t("faq-roles-a-link", "See how access is managed")}
        </MuiLink>
        {t(
          "faq-roles-a-suffix",
          " for a full list of roles and who can invite employees."
        )}
      </Typography>
    );
  }

  if (item.id === "product-vs-product-items") {
    return (
      <Typography variant="body1" component="p" sx={publicInfoBodySx}>
        {t(
          "faq-product-vs-product-items-a-prefix",
          "A product is your catalog listing, while product items are the stock records that hold quantities in each store."
        )}{" "}
        <MuiLink
          component={Link}
          to={aboutProductPath}
          underline="hover"
          sx={publicInfoLinkSx}
          data-testid="faq-ingress-about-product"
        >
          {t(
            "faq-product-vs-product-items-a-link",
            "See products vs. product items"
          )}
        </MuiLink>
        {t(
          "faq-product-vs-product-items-a-suffix",
          " for a full explanation with examples."
        )}
      </Typography>
    );
  }

  if (item.id === "support") {
    return (
      <Typography variant="body1" component="p" sx={publicInfoBodySx}>
        {t("faq-support-a-prefix", "Use the floating ")}{" "}
        <MuiLink
          component={Link}
          to={contactPath}
          underline="hover"
          sx={publicInfoLinkSx}
        >
          {t("faq-support-a-contact-link", "Contact us")}
        </MuiLink>{" "}
        {CONTACT_EMAIL ? (
          <>
            {t("faq-support-a-middle", " button, or ")}
            <MuiLink
              href={`mailto:${CONTACT_EMAIL}`}
              underline="hover"
              sx={publicInfoLinkSx}
            >
              {t("faq-support-a-email-link", "email support")}
            </MuiLink>
          </>
        ) : (
          t("faq-support-a-no-email-middle", " button")
        )}
        {t(
          "faq-support-a-suffix",
          ". We aim to respond within one business day."
        )}
      </Typography>
    );
  }

  return (
    <Typography variant="body1" component="p" sx={publicInfoBodySx}>
      {t(item.answerKey, item.answerFallback)}
    </Typography>
  );
};

export default FaqAnswer;
